import { useCallback, useEffect, useMemo, useRef } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Message } from '@shared/types';
import TextAreaChat from '@/components/TextAreaChat';
import { AssistantMessage } from '@/components/chat/AssistantMessage';
import { MeshChatStub } from '@/components/chat/MeshChatStub';
import { UserMessage } from '@/components/chat/UserMessage';
import { useConversation } from '@/services/conversationService';
import { AssistantLoading } from '@/components/chat/AssistantLoading';
import { ChatTitle } from '@/components/chat/ChatTitle';
import { CheckpointTimeline } from '@/components/chat/CheckpointTimeline';
import { InterruptInput } from '@/components/chat/InterruptInput';
import { CategorySelector } from '@/components/chat/CategorySelector';
import { TreeNode } from '@shared/Tree';
import { useParams } from 'react-router-dom';
import {
  useIsLoading,
  useSendContentMutation,
} from '@/services/messageService';
import { useTranslation } from 'react-i18next';

interface ChatSectionProps {
  messages: TreeNode<Message>[];
}

export function ChatSection({ messages }: ChatSectionProps) {
  const { t } = useTranslation('common');
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const { conversation } = useConversation();
  const { id: conversationId } = useParams();
  const isLoading = useIsLoading();
  const { mutate: sendMessage } = useSendContentMutation({ conversation });

  const scrollToBottom = useCallback(() => {
    if (scrollAreaRef.current) {
      const scrollContainer = scrollAreaRef.current.querySelector(
        '[data-radix-scroll-area-viewport]',
      );
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }
    }
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Also scroll when generating state changes
  useEffect(() => {
    if (isLoading) {
      scrollToBottom();
    }
  }, [isLoading, scrollToBottom]);

  const lastMessage = useMemo(() => {
    if (conversation.current_message_leaf_id) {
      return messages.find(
        (msg) => msg.id === conversation.current_message_leaf_id,
      );
    }
    return messages[messages.length - 1];
  }, [messages, conversation.current_message_leaf_id]);

  return (
    <div className="flex h-full w-full flex-col items-center overflow-hidden bg-k-canvas">
      <div className="flex w-full items-center justify-between gap-2 px-3 py-2 desktop:pl-10 desktop:pr-12">
        <div className="min-w-0 flex-1">
          <ChatTitle />
        </div>
        <CategorySelector
          conversationId={conversationId}
          value={conversation?.category}
        />
      </div>
      <CheckpointTimeline conversationId={conversationId} />
      <ScrollArea
        className="relative w-full min-w-0 max-w-2xl flex-1 px-3 py-0 sm:px-4"
        ref={scrollAreaRef}
      >
        <div className="min-w-0 space-y-3 overflow-hidden pb-4">
          {messages.map((message) => {
            return (
              <div className="p-1" key={message.id}>
                {message.role === 'assistant' ? (
                  <AssistantMessage message={message} />
                ) : (
                  <UserMessage message={message} isLoading={isLoading} />
                )}
              </div>
            );
          })}
          {isLoading && lastMessage?.role !== 'assistant' && (
            <AssistantLoading />
          )}
        </div>
      </ScrollArea>
      <div className="w-full max-w-2xl bg-synergy-bg-dark px-3 pb-3 pt-2 space-y-2 sm:px-4 sm:pb-4">
        {isLoading &&
          conversationId &&
          lastMessage?.role === 'assistant' && (
            <InterruptInput
              conversationId={conversationId}
              streamingMessageId={lastMessage.id}
            />
          )}
        {conversation.mode === 'mesh' || conversation.pendingMeshId ? (
          <MeshChatStub conversation={conversation} />
        ) : (
          <TextAreaChat
            onSubmit={sendMessage}
            placeholder={t('chat.messagePlaceholder')}
            disabled={isLoading}
            conversation={conversation}
          />
        )}
      </div>
    </div>
  );
}
